import React, { useEffect, useState } from "react";
import type { TableProps } from "antd";
import { Button, Table } from "antd";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import moment from "moment";
import { getCommonState, getRequestState } from "../../redux/store/getState";
import { RequestAtc } from "../../redux/apiSlice/requestSlice";
import modifyId from "../../utils/modifyId";
import { useNavigate } from "react-router-dom";
import useSearch from "../../hooks/useSearch";
import { getRequestsByAtc } from "../../redux/apiActions/requestsAction";
import successModal from "../../components/ConfirmationModal";
import { redCross } from "../../assets";
import { clearFilterValues } from "../../redux/apiSlice/commonSlice";
import { VOUCHER_STATUS } from "../../utils/status";
import useUser from "../../hooks/useUser";

type ColumnsType<T extends object = object> = TableProps<T>["columns"];

const ATCVoucherTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { requests, loading } = useAppSelector(getRequestState);
  const { filterFrom, filterTo, atc_id } = useAppSelector(getCommonState);
  const [data, setData] = useState<RequestAtc[]>();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const { user } = useUser();
  useSearch(requests, setData);

  useEffect(() => {
    if (user) {
      let filter = "";
      if (filterFrom && filterTo && atc_id)
        filter = `/filter?from=${filterFrom}&to=${filterTo}&exam_id=${atc_id}`;
      dispatch(getRequestsByAtc({ atc_id: user.atc.id, filter }));
    }
  }, [filterFrom, filterTo, atc_id, dispatch, user]);

  useEffect(() => {
    return () => {
      dispatch(clearFilterValues());
    };
  }, [dispatch]);

  const columns: ColumnsType<RequestAtc> = [
    {
      title: "Date and Time",
      dataIndex: "created_at",
      render: (created_at) =>
        `${moment(created_at).format("DD/MM/YYYY hh:mmA")}`,
    },
    {
      title: "Request ID",
      dataIndex: "id",
      render: (request_id) => <>{modifyId(request_id)}</>,
    },

    {
      title: "Exam ID",
      dataIndex: "exam",
      render: (exam) => <>{modifyId(exam.id)}</>,
    },
    {
      title: "Exam Name",
      dataIndex: "exam",
      render: (exam) => <>{exam.name}</>,
    },

    {
      title: "No. of Vouchers",
      dataIndex: "number_of_vouchers",
      render: (number_of_vouchers, record) => (
        <Button
          type="link"
          onClick={() => {
            if (record.status === "completed") {
              navigate(`/voucher-management/${record.id}`);
            }
          }}
          style={{ color: "white" }}
        >
          {number_of_vouchers}
        </Button>
      ),
    },
    {
      title: "Payment ID",
      dataIndex: "payment_id",
    },
    {
      title: "Invoice No.",
      dataIndex: "invoice_number",
    },
    {
      title: "Voucher Status",
      dataIndex: "status",

      render: (status, record) => (
        <>
          {record.status === "denied" ? (
            <div
              onClick={() => {
                successModal.show({
                  image: redCross,
                  text: record.comment,
                  manualClose: true,
                });
              }}
              style={{ color: "white", cursor: "pointer" }}
            >
              {VOUCHER_STATUS[status] || status}
            </div>
          ) : (
            VOUCHER_STATUS[status] || status
          )}
        </>
      ),
    },
  ];
  return (
    <Table
      style={{ marginTop: 20, whiteSpace: "pre" }}
      loading={loading.getRequest}
      columns={columns}
      rowKey={(record) => record.id}
      dataSource={data}
      onChange={(p) =>
        setPagination({ current: p.current || 1, pageSize: p.pageSize || 10 })
      }
      pagination={pagination}
      rowClassName={(_, index) => (index % 2 === 0 ? "tr-light" : "tr-dark")}
    />
  );
};

export default ATCVoucherTable;
